import React, { useState, useEffect } from 'react';
import { trackEvent } from '../utils/analytics';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl,
  InputLabel, Box, CircularProgress, Typography,
} from '@mui/material';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';

const baseUrl = process.env.REACT_APP_API_URL || 'https://sailarmada.com/api';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

const FEEDBACK_TYPES = ['Bug Report', 'Suggestion', 'General Feedback'];

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const [type, setType] = useState('Bug Report');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setType('Bug Report');
        setDescription('');
        setEmail('');
        setTurnstileToken('');
        setLoading(false);
        setSuccess(false);
        setError('');
      }, 300);
    }
  }, [isOpen]);

  useEffect(() => {
    if (success) {
      const t = setTimeout(onClose, 3000);
      return () => clearTimeout(t);
    }
  }, [success, onClose]);

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError('Please describe your feedback before submitting.');
      return;
    }
    if (!turnstileToken) {
      setError('Please complete the security check.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${baseUrl}/yachts/feedback/`, {
        type,
        description: description.trim(),
        email: email.trim(),
        pageUrl: window.location.href,
        turnstile_token: turnstileToken,
      });
      trackEvent('feedback_submitted', { feedback_type: type });
      setSuccess(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2, p: 1 } }}>
      <DialogTitle sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, pb: 0 }}>
        Send us Feedback
        <Typography variant="body2" sx={{ fontWeight: 400, color: 'text.secondary', mt: 0.5 }}>
          Looking for answers?{' '}
          <Link to="/help" onClick={onClose} style={{ color: '#E8368A' }}>
            Visit our Help page →
          </Link>
        </Typography>
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Thanks! 🎉</Typography>
            <Typography variant="body2" color="text.secondary">
              We received your feedback and will look into it. This window will close shortly.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select value={type} label="Type" onChange={e => setType(e.target.value)}>
                {FEEDBACK_TYPES.map(t => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Description"
              placeholder="Describe what happened or what you'd like to see..."
              multiline
              minRows={4}
              fullWidth
              size="small"
              value={description}
              onChange={e => setDescription(e.target.value)}
              required
            />

            <TextField
              label="Your email (optional)"
              placeholder="your@email.com (optional)"
              type="email"
              fullWidth
              size="small"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />

            <TextField
              label="Current page"
              value={window.location.href}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-input': { color: 'text.secondary', fontSize: '0.8rem' } }}
            />

            <Box sx={{
              transform: { xs: 'scale(0.82)', sm: 'scale(1)' },
              transformOrigin: 'left top',
              height: { xs: 54, sm: 'auto' },
            }}>
              <Turnstile
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={setTurnstileToken}
                onExpire={() => setTurnstileToken('')}
                onError={() => setTurnstileToken('')}
                options={{ theme: 'light' }}
              />
            </Box>

            {error && (
              <Typography variant="body2" color="error">{error}</Typography>
            )}
          </Box>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={onClose} variant="text" sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              background: '#E8368A',
              '&:hover': { background: '#c42d77' },
              fontWeight: 700,
              minWidth: 140,
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Send Feedback'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default FeedbackModal;
